import { Fragment, useCallback, useMemo, useState } from 'react'
import { connect, ConnectedProps } from 'react-redux'
import { createThreeBySearch } from './function'
import { faChevronDown, faChevronUp } from '@fortawesome/pro-light-svg-icons'
import TwinTrans from '../../TwinTrans'
import SearchBar from '../../SearchBar'
import useTwinTranslation from '../../../utils/hooks/useTwinTranslation'
import { CategoriesPayload, Category } from '../../../utils/reducers/products/categories'
import TwinIcon from '../../TwinIcon'
import Checkbox from '../../../forms/Checkbox'
import { AllReduxPayloads } from '../../../utils/reducers'
import withLoading from '../../../utils/hoc/withLoading'
import { ProductTypesKeys } from '@teinor/erp/types/company/product'
import RadioSelector from '../../../forms/RadioSelector'
import {  CategoryProductModelType } from '@teinor/erp/types/company/category/categoryProduct'
import { dictionaryFromJsonArr } from '../../../utils/globals/dictionary'
import { ChangeValueFunc } from '../../../utils/globals/types'
import { OnChangeMainCategoryTy } from './types'
import { productTypeCategoryTy } from '../../../utils/globals/product'

interface ProductCategoriesProps extends BaseProductCategoriesLogicProps {
    productType: ProductTypesKeys
    categoryProducts?: CategoryProductModelType[]
    className?: string
    readOnly?: boolean
}

const ProductCategories: React.FC<ProductCategoriesProps & ReduxCategories> = ({ className, categories, productType, categoryProducts, readOnly, selectedCategories = [], onChangeMain }) => {
    const { t } = useTwinTranslation()
    const parsedCategoryProducts = dictionaryFromJsonArr(categoryProducts || [], 'is_main', 'CategoryId')
    const indexCategory = productTypeCategoryTy[productType]
    const { memorizedTree, changeSearchValue, searchValue, parsedSelectedCategories, selectedMainCategory, changeSelectedMainCategory, handleCheckCategory } = useProductCategoriesLogic({ selectedCategories, treeCategory: categories?.organized?.[indexCategory]?.treeCategory || [], mainCategory: parsedCategoryProducts['true'] || 1, onChangeMain })
    if (!Object.keys(categories?.organized).length) {
        return null
    }
    return (
        <div className={className}>
            {!readOnly ? <SearchBar className='categories_searchbar' placeholder={t('searchByCategoryName', 'Busca por nombre de categoría')} onChange={changeSearchValue} {...{ searchValue }} />: null}
            <span className='regular14 text-right mr-40 block'>
                <TwinTrans transKey='mainCategory'>Categoría Principal</TwinTrans>
            </span>
            <TreeCategory treeCategory={memorizedTree} selectedCategories={parsedSelectedCategories} {...{ readOnly, searchValue, handleCheckCategory, changeSelectedMainCategory, selectedMainCategory }} />
            <input type='hidden' value={Array.from(parsedSelectedCategories).join(';')} name='categories' />
        </div>
    )
}
interface BaseProductCategoriesLogicProps {
    selectedCategories: number[]
    onChangeMain?: OnChangeMainCategoryTy
}
interface ProductCategoriesLogicProps extends BaseProductCategoriesLogicProps  {
    treeCategory: Category[]
    mainCategory: number
}
export type HandleCheckCategoryTy = (CategoryId: number, justAdd?: boolean | undefined) => void

const useProductCategoriesLogic = ({ selectedCategories, treeCategory, mainCategory, onChangeMain }: ProductCategoriesLogicProps) => {
    const [searchValue, setSearchValue] = useState<string>('')
    const [selectedMainCategory, setSelectedMainCategory] = useState<number>(mainCategory)
    const memorizedTree = useMemo(() => createThreeBySearch(treeCategory[0], searchValue) || [], [treeCategory, searchValue])
    const [parsedSelectedCategories, setParsedSelectedCategories] = useState<Set<number>>(new Set(selectedCategories))
    const changeSearchValue: ChangeValueFunc = useCallback((myvalue) => {
        setSearchValue(myvalue)
    }, [setSearchValue])
    
    const handleCheckCategory: HandleCheckCategoryTy = useCallback((CategoryId: number, justAdd?: boolean) => {
        setParsedSelectedCategories((old) => {
            const oldCopy = new Set([...Array.from(old)])
            if (!justAdd && old.has(CategoryId) && CategoryId !== selectedMainCategory) {
                oldCopy.delete(CategoryId)
            } else {
                oldCopy.add(CategoryId)
            }
            return oldCopy
        })
    }, [setParsedSelectedCategories, selectedMainCategory])

    const changeSelectedMainCategory = useCallback((CategoryId: number) => {
        setSelectedMainCategory(CategoryId)
        onChangeMain?.(CategoryId)
        handleCheckCategory(CategoryId, true)
    }, [setSelectedMainCategory, handleCheckCategory, onChangeMain])

    return { changeSearchValue, searchValue, parsedSelectedCategories, memorizedTree, selectedMainCategory, changeSelectedMainCategory, handleCheckCategory }
}

interface TreeCategoryProps {
    treeCategory: Category[]
    selectedCategories: Set<number>
    searchValue: string
    handleCheckCategory: HandleCheckCategoryTy
    selectedMainCategory: number
    readOnly?: boolean
    changeSelectedMainCategory: (CategoryId: number) => void
    className?: string
}

const TreeCategory: React.FC<TreeCategoryProps> = ({ treeCategory, className, readOnly, ...rest }) => {
    const renderThis: JSX.Element[] = []
    let deep = 1
    for (const category of treeCategory) {
        renderThis.push(
            <CategoryParent key={category.id} childs={category.childs} id={category.id} name={category['CategoryLangs.name']} {...{ deep, readOnly, ...rest }} />    
        )
    }
    return (
        <div className='-mx-10 flex flex-auto flex-col '>
            <div className={'flex px-10 overflow-auto max-h-600 flex-col parent_treecategory ' + (className || '') + (readOnly ? ' readOnly' : '') }>
                {renderThis}
            </div>
        </div>
    )
}

interface CategoryParentProps {
    name: string
    id: number
    selectedCategories: Set<number>
    deep: number
    searchValue: string
    selectedMainCategory: number
    changeSelectedMainCategory: (CategoryId: number) => void
    handleCheckCategory: HandleCheckCategoryTy
    childs?: Category[]
    readOnly?: boolean
    className?: string
}

const CategoryParent: React.FC<CategoryParentProps> = ({ name, id, selectedCategories, deep, className, handleCheckCategory, searchValue, selectedMainCategory, changeSelectedMainCategory, readOnly, childs = [] }) => {
    const { showChilds, handleShowChilds } = useCategoryParentLogic()
    const renderChilds: JSX.Element[] = []
    
    for (const category of childs) {
            renderChilds.push(
                <CategoryParent readOnly={readOnly} key={category.id} childs={category.childs} id={category.id} deep={deep + 1} name={category['CategoryLangs.name']} {...{ selectedCategories, handleCheckCategory, searchValue, selectedMainCategory, changeSelectedMainCategory }} className={className} />
            )
    }
    return (
        <Fragment>
            <div style={{ marginLeft: (deep * 10) + 'px' }} key={id} className='pt-10 flex items-center justify-between category_container' >
                <CategoryCheckbox name={name} CategoryId={id} checked={selectedCategories.has(id) ? true : false} handleCheckCategory={handleCheckCategory} readOnly={readOnly} className={ className} />
                <div className='flex items-center'>
                    <RadioSelector name='categoryIsMain' value={selectedMainCategory} defaultChecked={id === selectedMainCategory ? true : false} onClick={() => { changeSelectedMainCategory(id) }} disabled={readOnly} />
                    <ArrowParent hasChilds={childs?.length ? true : false} onClick={handleShowChilds} showChilds={showChilds} />
                </div>
            </div>
            {showChilds&&
                <div className='mt-5'>
                    {renderChilds}
                </div>
            }
        </Fragment>
    )
    
}

const useCategoryParentLogic = () => {
    const [showChilds, setShowChilds] = useState<boolean>(true)

    const handleShowChilds = useCallback(() => {
        setShowChilds((oldShow)=> !oldShow)
    }, [setShowChilds])

    return { showChilds, handleShowChilds  }
}

interface ArrowParentProps {
    onClick: () => void
    showChilds: boolean
    hasChilds?: boolean
}

const ArrowParent: React.FC<ArrowParentProps> = ({ onClick, showChilds, hasChilds = false }) => {
    if (!hasChilds) {
        return <div className='not_arrow_categories'></div>
    }
    
    return (
        <TwinIcon icon={showChilds ? faChevronUp : faChevronDown} onClick={onClick} className='cursor-pointer arrow_categories'/>
    )
}

export interface CategoryCheckboxProps {
    name: string
    checked: boolean
    handleCheckCategory: HandleCheckCategoryTy
    CategoryId: number
    readOnly?: boolean
    className?: string
}

const CategoryCheckbox: React.FC<CategoryCheckboxProps> = ({ name, checked, handleCheckCategory, CategoryId, className, readOnly = false }) => {
    return (
        <Checkbox name='category' label={name} checked={checked} onChange={() => handleCheckCategory(CategoryId)} disabled={readOnly} className={className} />
    );
}

const categoriesDispatch = {
    setCategories: (payload: CategoriesPayload) => ({ type: 'CHANGE_CATEGORIES', payload }),
}

export type ReduxCategories = ConnectedProps<typeof categoriesConnect>
const mapCategoriesConnector = (state: AllReduxPayloads) => ({ categories: state.categories })
const categoriesConnect = connect(mapCategoriesConnector, categoriesDispatch)

const categoriesConnectLoading = withLoading(ProductCategories, [{ fetchUrl: '/api/app/product/category/getAllCategories', propName: 'categories', setFunctionName: 'setCategories' }])

export default categoriesConnect(categoriesConnectLoading);